import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, FolderOpen, ArrowLeft, Download, FileText, FileArchive, Files, Eye } from "lucide-react";
import { toast } from "sonner";

export default function CustomerFolders() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [folders, setFolders] = useState(null);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const firmId = user.firm_id;

  const open = async (f) => {
    setActive(f); setLoading(true);
    try { const { data } = await api.get(`/firms/${firmId}/documents?folder=${f.code}`); setDocs(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!firmId) { setFolders([]); return; }
    api.get(`/firms/${firmId}/folders-detail`).then(({ data }) => {
      setFolders(data.folders);
      const code = params.get("folder");
      if (code) { const f = data.folders.find((x) => x.code === code); if (f) open(f); }
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [firmId]); // eslint-disable-line

  if (!firmId) return <p className="text-sm text-slate-500">Firma bağlantınız bulunamadı.</p>;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold text-slate-900">Belge Klasörlerim</h1>
      {folders === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> :
        !active ? (
          <>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadFile(`/firms/${firmId}/download-zip`, "belgelerim.zip")} data-testid="customer-bulk-zip"><FileArchive className="h-4 w-4 mr-2" /> Tümü ZIP</Button>
              <Button variant="outline" size="sm" onClick={() => downloadFile(`/firms/${firmId}/download-pdf`, "belgelerim.pdf")} data-testid="customer-bulk-pdf"><Files className="h-4 w-4 mr-2" /> Tümü PDF</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {folders.map((f) => (
                <Card key={f.code} className="border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => open(f)} data-testid={`customer-folder-${f.code}`}>
                  <CardContent className="pt-5 flex items-center gap-3">
                    <FolderOpen className={`h-6 w-6 ${f.type === "activity" ? "text-sky-500" : "text-amber-500"}`} />
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{f.name}</div><div className="text-[11px] text-slate-400">{f.file_count} belge</div></div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-sm text-slate-400">Yalnızca firmanız için yayımlanan belgeler görünür.</p>
          </>
        ) : (
          <Card className="border-slate-200"><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="customer-folder-back"><ArrowLeft className="h-4 w-4 mr-2" /> Klasörler</Button>
              <div className="font-display font-semibold text-slate-800">{active.name}</div>
            </div>
            {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> :
              docs.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">Bu klasörde yayımlanmış belge yok.</p> : (
                <div className="divide-y divide-slate-100">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-3" data-testid={`customer-doc-${d.id}`}>
                      <div className="flex items-center gap-3"><FileText className="h-5 w-5 text-slate-400" /><span className="text-sm font-medium text-slate-800">{d.name}.{d.ext}</span><StatusBadge tone="active">Yayımlandı</StatusBadge></div>
                      <div className="flex gap-1">
                        {d.ext === "pdf" && <Button variant="ghost" size="sm" onClick={() => previewFile(`/documents/${d.id}/download`)} data-testid={`customer-doc-preview-${d.id}`} title="Önizle"><Eye className="h-4 w-4" /></Button>}
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${d.id}/download`, `${d.name}.${d.ext}`)} data-testid={`customer-doc-download-${d.id}`} title="İndir"><Download className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent></Card>
        )}
    </div>
  );
}
